import { filter, pipe, tap } from "wonka";
import type { Exchange, Operation, OperationResult } from "@urql/core";
import {
  actionOnGetCreateNodeResponse,
  exeCreateNodeFromQueue,
} from "api/exchanges/nodeCreate";
import {
  actionOnGetCopyNodeResponse,
  exeCopyNodeFromQueue,
} from "api/exchanges/nodeCopy";
import { exeUpdateNodeFromQueue } from "api/exchanges/nodeUpdate";
import { exeDeleteNodeFromQueue } from "api/exchanges/nodeDelete";
import type {
  ExchangeClient,
  ExchangeOperation,
  ExchangeResponse,
} from "api/exchanges/types";

let requestInProgress = false;

export const pauseExchange: Exchange = ({ forward, client }) => {
  const queue = new Set<ExchangeOperation>();
  const replaceKeysMap = new Map<string, string>();

  return (operations$) => {
    const tapStep = tap((operation: Operation) => {
      const queueOperation = operation as ExchangeOperation;
      if (
        queueOperation.context.projectPauseExchangeType &&
        queueOperation.context.optimistic === true
      ) {
        if (requestInProgress) {
          queue.add(queueOperation);
        } else {
          requestInProgress = true;
        }
      }
    });
    const filterStep = filter(
      (operation: Operation) => !queue.has(operation as ExchangeOperation),
    );

    const sharedOps$ = pipe(operations$, tapStep, filterStep);

    const tapResponse = tap((response: OperationResult) => {
      const exchangeResponse = response as ExchangeResponse;
      const responseOperation = exchangeResponse.operation;

      if (responseOperation.context.projectPauseExchangeType) {
        const pauseType = responseOperation.context.projectPauseExchangeType;
        switch (pauseType) {
          case "nodeCreate":
            actionOnGetCreateNodeResponse({
              response: exchangeResponse,
              queue,
              replaceKeysMap,
            });
            break;
          case "nodeCopy":
            actionOnGetCopyNodeResponse({
              response: exchangeResponse,
              queue,
              replaceKeysMap,
            });
            break;
          case "nodeUpdate":
          case "nodeDelete":
          case "moveConfig":
          case "updateConfig":
          case "deleteConfig":
          case "addConfig":
          case "copyConfig":
          case "uploadTroxNetCom":
            break;
          default:
            console.error(pauseType, "not specified");
        }
        if (queue.size === 0) {
          requestInProgress = false;
          replaceKeysMap.clear();
        } else {
          const operation = queue.values().next().value;
          if (!operation) {
            requestInProgress = false;
            replaceKeysMap.clear();
            return;
          }

          const operationType = operation.context.projectPauseExchangeType;
          switch (operationType) {
            case "nodeCreate":
              exeCreateNodeFromQueue({
                operation,
                client: client as ExchangeClient,
                queue,
                replaceKeysMap,
              });
              break;
            case "nodeCopy":
              exeCopyNodeFromQueue({
                operation,
                client: client as ExchangeClient,
                queue,
                replaceKeysMap,
              });
              break;
            case "nodeUpdate":
              exeUpdateNodeFromQueue({
                operation,
                client: client as ExchangeClient,
                queue,
                replaceKeysMap,
              });
              break;
            case "nodeDelete":
              exeDeleteNodeFromQueue({
                operation,
                client: client as ExchangeClient,
                queue,
                replaceKeysMap,
              });
              break;
            case "uploadTroxNetCom":
            case "moveConfig":
            case "updateConfig":
            case "deleteConfig":
            case "addConfig":
            case "copyConfig":
              break;
            default:
              console.error(operationType, "not specified");
          }
        }
      }
    });

    return pipe(forward(sharedOps$), tapResponse);
  };
};
